import { useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";
import { formatDate, formatDuration, formatViews, videoPath } from "@/lib/videos";

interface MetaPanelProps {
  video: Video;
  isFavorite: boolean;
  onToggleFavorite: () => void;
}

export default function MetaPanel({ video, isFavorite, onToggleFavorite }: MetaPanelProps) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const rows = [
    { label: "导演", value: video.director },
    { label: "制片", value: video.producer },
    { label: "地区", value: video.region },
    { label: "上映年份", value: `${video.year}年` },
    { label: "时长", value: formatDuration(video.duration) },
    { label: "发布日期", value: formatDate(video.publishDate) },
  ];

  return (
    <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 leading-tight">{video.title}</h2>

      <div className="mt-3 flex items-center gap-2.5 flex-wrap text-xs">
        <span className="px-2 py-0.5 rounded-sm bg-primary-500 text-background-50 font-medium">
          {video.type === "movie" ? "电影" : "短视频"}
        </span>
        <span className="text-accent-400 flex items-center gap-1 font-medium">
          <i className="ri-star-fill" />
          {video.rating.toFixed(1)}
        </span>
        <span className="text-foreground-400">{formatViews(video.views)}</span>
        <span className="w-0.5 h-0.5 rounded-full bg-foreground-700" />
        <span className="text-foreground-400">{formatDuration(video.duration)}</span>
        <span className="w-0.5 h-0.5 rounded-full bg-foreground-700" />
        <span className="text-foreground-400">{video.region}</span>
      </div>

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={onToggleFavorite}
          className={`h-10 px-4 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            isFavorite
              ? "bg-primary-500 text-background-50 hover:bg-primary-600"
              : "bg-secondary-800 text-secondary-100 hover:bg-secondary-700"
          }`}
        >
          <i className={isFavorite ? "ri-heart-fill" : "ri-heart-3-line"} />
          {isFavorite ? "已收藏" : "收藏作品"}
        </button>
        <button
          type="button"
          onClick={copyLink}
          className="h-10 px-4 rounded-md border border-background-300/70 text-sm text-foreground-200 hover:border-primary-400 hover:text-foreground-50 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
        >
          <i className={copied ? "ri-check-line text-accent-400" : "ri-link"} />
          {copied ? "链接已复制" : "复制链接"}
        </button>
        {isFavorite && (
          <span className="text-[11px] text-foreground-500">收藏已保存在本浏览器，无需登录。</span>
        )}
      </div>

      <dl className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 pt-5 border-t border-background-300/40">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="text-[11px] text-foreground-600">{row.label}</dt>
            <dd className="mt-0.5 text-xs text-foreground-200 truncate" title={row.value}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-5">
        <h3 className="text-xs text-foreground-600 mb-1.5">主演</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {video.cast.map((name) => (
            <span key={name} className="px-2 py-1 rounded-sm bg-background-200/70 text-[11px] text-foreground-300">
              {name}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <h3 className="text-xs text-foreground-600 mb-1.5">所属分类</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={video.type === "movie" ? "/category/movie" : "/category/short"}
            className="flex items-center gap-1 h-7 px-2.5 rounded-full bg-primary-100/15 text-primary-300 text-[11px] hover:bg-primary-500/25 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-folder-line" />
            {video.category}
          </Link>
          <Link
            to="/category/theme"
            className="flex items-center gap-1 h-7 px-2.5 rounded-full bg-secondary-800/80 text-secondary-200 text-[11px] hover:bg-secondary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-fire-line" />
            热门主题
          </Link>
          {video.tags.map((tag) => (
            <Link
              key={tag}
              to={`/search?q=${encodeURIComponent(tag)}`}
              className="flex items-center gap-1 h-7 px-2.5 rounded-full border border-background-300/60 text-[11px] text-foreground-400 hover:border-accent-500/60 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              #{tag}
            </Link>
          ))}
        </div>
      </div>

      <p className="mt-5 text-[11px] text-foreground-600">
        演示页面 · <Link to="/about" className="underline hover:text-foreground-300">版权声明与使用说明</Link> · 分享链接：
        <span className="text-foreground-500"> {videoPath(video.id)}</span>
      </p>
    </div>
  );
}